import type { BillingInterval } from 'definitions/billing-plans';
import { useState } from 'react';
import { agencyFeePeriodPrice } from 'definitions/billing-plans';
import { useAuth } from 'hooks/useAuth';
import { useBillingState } from './useBillingState';
import { useCheckout } from './useCheckout';

/**
 * The agency fee: the agency features, bought once by the agency and independent of
 * how many clients it has (it works with none). Each client then has a plan of its
 * own; this is not a plan and has no sessions or credits.
 *
 * Used by the welcome page (an agency's one setup step) and Settings > Billing. Buying
 * is for Owners and Admins; the server refuses anyone else.
 */
export const useAgencyFee = () => {
  const { user } = useAuth();
  const { billingState } = useBillingState();
  const checkout = useCheckout();
  const [chosenInterval, setChosenInterval] = useState<BillingInterval | null>(null);

  const fee = billingState?.agencyFee ?? null;
  const interval = chosenInterval ?? fee?.interval ?? 'month';
  const periodPrice = agencyFeePeriodPrice(interval);

  return {
    fee,
    isLive: !!fee?.live,
    canManage: !!user && user.role !== 'Member',
    interval,
    setInterval: setChosenInterval,
    periodPrice,
    monthlyEquivalent: interval === 'year' ? Math.round(periodPrice / 12) : periodPrice,
    trialAvailable: !!billingState?.agencyFeeTrialAvailable,
    checkoutAvailable: billingState?.checkoutAvailable ?? false,
    isStarting: checkout.isStarting,
    start: () => checkout.startAgencyFee(interval),
  };
};

export type AgencyFeeManager = ReturnType<typeof useAgencyFee>;
